import React, { useEffect, useState, useRef } from "react";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import PostCard from "../components/PostCard";
import Calendar from "react-calendar";
import { Calendar as CalendarIcon, Lock, AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import API from "../utils/axiosInstance";
import "./Subscriptions.css";
import "react-calendar/dist/Calendar.css";


const Subscriptions = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [subscriptions, setSubscriptions] = useState([]);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const { t, i18n } = useTranslation();
  const calendarRef = useRef(null);

  useEffect(() => {
    fetchUserSubscriptions();
  }, []);

  const fetchUserSubscriptions = async () => {
    try {
      setLoading(true);
      const res = await API.get("/subscriptions");
      const subs = res.data || [];
      setSubscriptions(subs);
      
      // Auto-select first subscription
      if (subs.length > 0) {
        setSelectedSubscription(subs[0]);
      }
    }catch (err) {
  console.error("Failed to fetch subscriptions:", err);
  setSubscriptions([]);
} finally {
      setLoading(false);
    }
  };

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };

    if (showCalendar) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCalendar]);

  useEffect(() => {
    if (selectedSubscription) {
      fetchSubscriptionPosts();
    }
  }, [selectedSubscription]);

  const fetchSubscriptionPosts = async () => {
    try {
      setLoading(true);
      const res = await API.get(`/subscriptions/posts/${selectedSubscription.id}`);
      const fetchedPosts = res.data || [];
      setPosts(fetchedPosts);
      setFilteredPosts(fetchedPosts);
    } catch (err) {
      console.error("Failed to fetch posts:", err);
      
      // Check if subscription is locked/expired
      if (err.response?.status === 403) {
        const errorData = err.response.data;
        if (errorData.locked) {
          // Show locked state
          setPosts([]);
          setFilteredPosts([]);
        }
      } else {
        setPosts([]);
  setFilteredPosts([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedDate) {
      const filtered = posts.filter((post) => {
        const postDate = new Date(post.createdAt);
        return (
          postDate.getDate() === selectedDate.getDate() &&
          postDate.getMonth() === selectedDate.getMonth() &&
          postDate.getFullYear() === selectedDate.getFullYear()
        );
      });
      setFilteredPosts(filtered);
    } else {
      setFilteredPosts(posts);
    }
  }, [selectedDate, posts]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setShowCalendar(false);
  };

  const clearDateFilter = () => {
    setSelectedDate(null);
  };

  return (
    <div 
      className="dashboard-wrapper" 
      dir={["ar","fa","ir","he"].includes(i18n.language)?"rtl":"ltr"}
      style={{
        flexDirection: ["ar","fa","ir","he"].includes(i18n.language) ? "row-reverse" : "row",
      }}
    >
      <div className="sidebar-fixed"><Sidebar isOpen={isSidebarOpen} /></div>
      <div className="dashboard-main">
        <Navbar onToggleSidebar={() => setIsSidebarOpen((p) => !p)} />
        
        <div className="subscriptions-content">
          <div className="subscriptions-header">
            <h2>{t("Subscriptions") || "Subscriptions"}</h2>
            <p className="subscriptions-subtitle">
              {t("SubscriptionForumDesc") || "Join our community forum and stay updated with the latest discussions"}
            </p>
          </div>

          {loading ? (
            <div className="loading-state">
              <p>{t("LoadingSubscriptions") || "Loading subscriptions..."}</p>
            </div>
          ) : subscriptions.length === 0 ? (
            <div className="empty-state">
              <p>{t("NoSubscriptions") || "No subscriptions found"}</p>
            </div>
          ) : (
            <div className="subscriptions-layout">
              {/* Left Side - Topics List */}
              <div className="topics-sidebar">
                <h3 className="topics-heading">{t("Topics")}</h3>
                <div className="topics-list">
                  {subscriptions.map((sub) => {
                    const isExpired = sub.access && !sub.access.isValid;
                    const daysRemaining = sub.access?.daysRemaining || 0;
                    
                    return (
                      <div
                        key={sub.id}
                        className={`topic-item ${selectedSubscription?.id === sub.id ? 'active' : ''} ${isExpired ? 'expired' : ''}`}
                        onClick={() => setSelectedSubscription(sub)}
                      >
                        <div className="topic-icon">
                          {isExpired ? <Lock size={18} /> : '📌'}
                        </div>
                        <div className="topic-info">
                          <h4 className="topic-title">
                            {sub.title}
                            {isExpired && <Lock size={14} style={{ marginLeft: '6px', color: '#ff4757' }} />}
                          </h4>
                          <p className="topic-desc">{sub.description}</p>
                          {sub.access && (
                            <div className="subscription-status">
                              {isExpired ? (
                                <span className="status-badge expired">
                                  Expired - Renew to access
                                </span>
                              ) : daysRemaining <= 7 ? (
                                <span className="status-badge expiring-soon">
                                  <AlertTriangle size={12} /> {daysRemaining} days left
                                </span>
                              ) : (
                                <span className="status-badge active">
                                  Active - {daysRemaining} days left
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Right Side - Content Area */}
              <div className="content-area">
                {selectedSubscription ? (
                  <>
                    <div className="content-header">
                      <div className="content-title-section">
                        <h3 className="content-title">{selectedSubscription.title}</h3>
                        <p className="content-subtitle">{selectedSubscription.description}</p>
                      </div>
                      <div className="content-filters">
                        {/* <button 
                          className="calendar-toggle-btn"
                          onClick={() => setShowCalendar(!showCalendar)}
                          title="Filter by date"
                        > */}
                            <button
  className="calendar-toggle-btn"
  title="Filter by date"
  onClick={(e) => {
    e.stopPropagation();
    setShowCalendar((prev) => !prev);
    
  }}
>
                          <CalendarIcon size={18} />
                          {selectedDate ? (
                            <span className="filter-date-label">
                              {selectedDate.toLocaleDateString()}
                            </span>
                          ) : (
                            <span>{t("Filter By Date")}</span>
                          )}
                        </button>
                        {selectedDate && (
                          <button 
                            className="clear-filter-btn"
                            onClick={clearDateFilter}
                            title="Clear date filter"
                          >
                            ✕ Clear
                          </button>
                        )}
                      </div>
                    </div>

                    {showCalendar && (
                      <div className="calendar-dropdown" ref={calendarRef}>
                        <Calendar
                          onChange={handleDateChange}
                          value={selectedDate}
                          maxDate={new Date()}
                           tileClassName={({ date, view }) => {
    if (view === "month") {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (date > today) {
        return "future-date";
      }
    }
    return null;
  }}
                        />
                      </div>
                    )}

                    <div className="posts-feed">
                      {selectedSubscription?.access && !selectedSubscription.access.isValid ? (
                        <div className="locked-content">
                          <Lock size={64} className="lock-icon" />
                          <h3>Subscription Expired</h3>
                          <p>
                            Your subscription to <strong>{selectedSubscription.title}</strong> expired on{' '}
                            {new Date(selectedSubscription.access.endDate).toLocaleDateString()}.
                          </p>
                          <p>Please renew your subscription to continue accessing this content.</p>
                          <button className="renew-button" onClick={() => {
                            // TODO: Redirect to payment/renewal page
                            alert('Renewal functionality coming soon!');
                          }}>
                            Renew Subscription
                          </button>
                        </div>
                      ) : filteredPosts.length === 0 ? (
                        <div className="no-posts">
                          <p>
                            {selectedDate 
                              ? "No posts found for the selected date." 
                              : "No posts available yet."}
                          </p>
                        </div>
                      ) : (
                        filteredPosts.map((post) => (
                          <PostCard key={post.id} post={post} />
                        ))
                      )}
                    </div>
                  </>
                ) : (
                  <div className="no-topic-selected">
                    <p>Select a topic to view posts</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Subscriptions;
